'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import * as React from 'react';
import PropTypes from 'prop-types';
import makeDecoratorDisplayName from 'reference-resolvers/lib/makeDecoratorDisplayName';
import { makeAtom } from 'atom';
import { Map as ImmutableMap } from 'immutable';
import map from 'transmute/map';
import curry from 'transmute/curry';
import identity from 'transmute/identity';
import memoizeOne from 'react-utils/memoizeOne';
import ReferenceResolversContext from './ReferenceResolversContext';
import withReferenceResolverContext from './withReferenceResolverContext';
import { referenceResolverContextPropTypes } from './ReferenceResolversContext'; // Adapted from https://ramdajs.com/docs/#binary
// Wraps a function of any arity (including nullary) in a function that accepts exactly 2 parameters. Any extraneous parameters will not be passed to the supplied function.

function binary(fn) {
  return function (a0, a1) {
    return fn.call(this, a0, a1);
  };
} // The default merge will favor the higher level cached resolvers.


const defaultMergeResolvers = (providedResolvers, connectedResolvers) => Object.assign({}, providedResolvers, {}, connectedResolvers); // Note: `this.mergeResolvers()` is called with three args but we only want the first two to
// factor into memoization so we force the function to be binary


const getMergeResolversFn = mergeResolvers => mergeResolvers instanceof Function ? mergeResolvers : mergeResolvers === true ? binary(memoizeOne(defaultMergeResolvers)) : identity;

const ProvideReferenceResolvers = (resolvers, Component, opts = {}) => {
  const {
    mergeResolvers
  } = opts;

  class ReferenceResolversProvider extends React.Component {
    constructor() {
      super();
      this.getCacheAtom = this.getCacheAtom.bind(this);
      this._cacheAtoms = ImmutableMap();
      this.providedResolvers = map(resolver => {
        if (resolver instanceof Function) {
          return resolver(this.getCacheAtom);
        }

        return resolver;
      }, resolvers);
      this.mergeResolvers = getMergeResolversFn(mergeResolvers);
    }

    getCacheAtom(id) {
      if (!this._cacheAtoms.has(id)) {
        this._cacheAtoms = this._cacheAtoms.set(id, makeAtom());
      }

      return this._cacheAtoms.get(id);
    }

    render() {
      const _this$props = this.props,
            {
        forwardedRef,
        referenceResolverContext: connectedResolvers
      } = _this$props,
            rest = _objectWithoutPropertiesLoose(_this$props, ["forwardedRef", "referenceResolverContext"]);

      const mergedResolvers = this.mergeResolvers(this.providedResolvers, connectedResolvers, // Note: If we can get rid of the `mergeResolvers` as function option we can drop this third `rest` param
      // and simplify the memozation code above.
      rest);
      return /*#__PURE__*/_jsx(ReferenceResolversContext.Provider, {
        value: mergedResolvers,
        children: /*#__PURE__*/_jsx(Component, Object.assign({
          ref: forwardedRef
        }, rest))
      });
    }

  }

  ReferenceResolversProvider.propTypes = Object.assign({}, referenceResolverContextPropTypes, {
    forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
      current: PropTypes.object
    })])
  });
  const ContextConnector = withReferenceResolverContext(ReferenceResolversProvider);
  ContextConnector.displayName = makeDecoratorDisplayName('ProvideReferenceResolvers', Component);
  ContextConnector.WrappedComponent = Component;

  if (opts.forwardRef) {
    const ForwardedRef = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(ContextConnector, Object.assign({}, props, {
      forwardedRef: ref
    })));
    ForwardedRef.WrappedComponent = ContextConnector.WrappedComponent;
    return ForwardedRef;
  }

  return ContextConnector;
};

export default curry(ProvideReferenceResolvers);